import { type HTMLComponent } from "@commercekit/react";
import type {
  LoqateVerifyMatch,
  LoqateVerifyResponse,
} from "@commercekit/shared";
import classNames from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLoqateContext } from "~/hooks/useLoqate";
import { usePrismicLabels } from "~/hooks/usePrismicLabel";

interface WvAddressSuggestionModalProps {
  modalData: LoqateVerifyResponse[];
  onClose: (proceedImmediately?: boolean) => void;
  isOpen: boolean;
  applySuggestion?: (suggestion: LoqateVerifyMatch, addressType?: "billing" | "shipping") => void;
}

interface TemporarySelection {
  suggestion: LoqateVerifyMatch;
  addressType: "billing" | "shipping";
}

// Helper function for formatting addresses
const formatAddress = (suggestion: LoqateVerifyMatch): string => {
  const premiseAndSubBuilding = [
    suggestion.PremiseNumber ?? suggestion.Premise,
    suggestion.SubBuildingNumber ?? suggestion.SubBuilding,
  ]
    .filter((value) => (typeof value === "string" ? value.trim() !== "" : Boolean(value)))
    .join(" ")
    .trim();

  // Prepend Building/BuildingName to the Thoroughfare/Address1 for display
  const firstPart = [
    suggestion.Building || suggestion.BuildingName || suggestion.Company,
    suggestion.Thoroughfare ?? suggestion.Address1,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  const parts = [
    firstPart,
    premiseAndSubBuilding || undefined,
    suggestion.PostalCode,
    suggestion.Locality,
  ].filter((value) => (typeof value === "string" ? value.trim() !== "" : Boolean(value)));

  return parts.join(", ");
};

// Suggestion Item Component
const SuggestionItem = ({
  suggestion,
  addressType,
  isSelected,
  onSelect,
}: {
  suggestion: LoqateVerifyMatch;
  addressType: "billing" | "shipping";
  isSelected: boolean;
  onSelect: (
    suggestion: LoqateVerifyMatch,
    addressType: "billing" | "shipping"
  ) => void;
}) => {
  return (
    <button
      type="button"
      className={classNames(
        "p-3 mb-3 text-left bg-white border rounded suggestion-item w-100",
        {
          active: isSelected,
        }
      )}
      onClick={() => onSelect(suggestion, addressType)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(suggestion, addressType);
        }
      }}
      role="listitem"
      aria-label={`Select ${addressType} address: ${formatAddress(suggestion)}`}
      style={{ cursor: "pointer", transition: "all 0.2s ease" }}
    >
      <div className="suggestion-address d-flex justify-content-between align-items-center">
        <div>
          <strong className="mb-1 d-block">{formatAddress(suggestion)}</strong>
          {suggestion.CountryName && <small>{suggestion.CountryName}</small>}
        </div>
        {isSelected && <div className="text-success">✓</div>}
      </div>
    </button>
  );
};

// Modal Content Component
const AddressSuggestionContent = ({
  modalData,
  hasMultipleAddresses,
  temporarySelections,
  onSuggestionSelect,
  onProceedAnyway,
  bypassValidation,
  descriptionId,
  $pt,
}: {
  modalData: LoqateVerifyResponse[];
  hasMultipleAddresses: boolean;
  temporarySelections: Record<
    "billing" | "shipping",
    TemporarySelection | null
  >;
  onSuggestionSelect: (
    suggestion: LoqateVerifyMatch,
    addressType: "billing" | "shipping"
  ) => void;
  onProceedAnyway: () => void;
  bypassValidation: boolean;
  descriptionId: string;
  $pt: (key: string) => string;
}) => (
  <div className="p-4" style={{ overflowY: "auto", maxHeight: "60vh" }}>
    <p id={descriptionId} className="mb-4 text-muted">
      {hasMultipleAddresses
        ? $pt("address_suggestion_desc_multiple")
        : $pt("address_suggestion_desc")}
    </p>

    <div className="address-suggestions" role="list">
      {modalData.map((addressData, addressIndex) => (
        <div key={addressIndex} className="mb-4">
          {/* Show section header if multiple addresses */}
          {hasMultipleAddresses && (
            <div className="mb-3">
              <h5 className="text-uppercase text-muted text small">
                {addressData.addressType === "billing"
                  ? $pt("checkout_billing_address_title")
                  : $pt("checkout_shipping_address_title")}
              </h5>
              <hr className="mt-2" />
            </div>
          )}

          {addressData.suggestions.map((suggestion, index) => {
            const isSelected =
              temporarySelections[addressData.addressType]?.suggestion ===
                suggestion;

            return (
              <SuggestionItem
                key={`${addressIndex}-${index}`}
                suggestion={suggestion}
                addressType={addressData.addressType}
                isSelected={isSelected}
                onSelect={onSuggestionSelect}
              />
            );
          })}
        </div>
      ))}
    </div>

    {/* OR separator and proceed anyway action */}
    <div className="mt-4">
      {/* Same heading level as the main modal title */}
      <p className="mb-4 text-center text-muted text-uppercase">
        {$pt("joiner_or")}
      </p>
      <button
        type="button"
        className={classNames(
          "p-3 mb-3 text-left bg-white border rounded suggestion-item w-100",
          {
            active: bypassValidation,
          }
        )}
        onClick={onProceedAnyway}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onProceedAnyway();
          }
        }}
        aria-label="Proceed anyway without correcting the address"
        style={{ cursor: "pointer", transition: "all 0.2s ease" }}
      >
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <strong className="mb-1 d-block">{$pt("proceed_anyway")}</strong>
          </div>
          {bypassValidation && <div className="text-success">✓</div>}
        </div>
      </button>
    </div>
  </div>
);

// Modal Header Component
const AddressSuggestionHeader = ({
  onClose,
  $pt,
}: {
  onClose: () => void;
  $pt: (key: string) => string;
}) => (
  <div className="p-4 modal-header d-flex justify-content-between align-items-center">
    <h4 id="modal-title-desktop" className="mb-0 heading strong">
      {$pt("address_suggestion_title")}
    </h4>
    <button
      type="button"
      className="p-2 btn-close"
      onClick={onClose}
      aria-label="Close modal"
      style={{ cursor: "pointer" }}
    >
      ×
    </button>
  </div>
);
// Modal Footer Component
const AddressSuggestionFooter = ({
  hasValidSelections,
  onClose,
  onDone,
  $pt,
  bypassValidation,
}: {
  hasValidSelections: boolean;
  onClose: () => void;
  onDone: () => void;
  $pt: (key: string) => string;
  bypassValidation: boolean;
}) => (
  <div className="p-4 modal-footer d-flex justify-content-end">
    <div className="gap-2 d-flex">
      <button type="button" className="btn btn-danger" onClick={onClose}>
        {$pt("back_to_checkout")}
      </button>
      <button
        type="button"
        className="btn btn--primary-green"
        onClick={onDone}
        disabled={!hasValidSelections && !bypassValidation}
      >
        {$pt("proceed_to_payment")}
      </button>
    </div>
  </div>
);

const WvAddressSuggestionModal: HTMLComponent<
  WvAddressSuggestionModalProps
> = ({ modalData, onClose, isOpen, applySuggestion }) => {
  const { $pt } = usePrismicLabels();
  const {
    applySuggestion: applySuggestionCheckout,
    setBypassValidation,
    bypassValidation,
  } = useLoqateContext();
  const modalRef = useRef<HTMLDivElement>(null);

  // Check if we have multiple addresses
  const hasMultipleAddresses = modalData && modalData.length > 1;

  // Temporary selection state for multi-address scenarios
  const [temporarySelections, setTemporarySelections] = useState<
    Record<"billing" | "shipping", TemporarySelection | null>
  >(() => {
    // Initialize with first suggestion for each address type if modal is open and has data
    if (isOpen && modalData && modalData.length > 0) {
      const initialSelections: Record<"billing" | "shipping", TemporarySelection | null> = {
        billing: null,
        shipping: null
      };

      // Process all items in modalData to handle both shipping and billing
      modalData.forEach((addressData) => {
        const addressType = addressData?.addressType;
        const suggestion = addressData?.suggestions?.[0];
        if (addressType && suggestion && (addressType === "billing" || addressType === "shipping")) {
          initialSelections[addressType] = { suggestion, addressType };
        }
      });

      return initialSelections;
    }
    return { billing: null, shipping: null };
  });

  // Check if user has made valid selections for multiple addresses
  const hasValidSelections = Object.values(temporarySelections).some(
    (v) => v !== null
  );

  // Handle applying all temporary selections
  const handleDoneClick = useCallback(() => {
    // Apply all temporary selections
    Object.values(temporarySelections).forEach((selection) => {
      if (selection) {
        applySuggestion
         ? applySuggestion(selection.suggestion)
         : applySuggestionCheckout(selection.suggestion, selection.addressType);
      }
    });

    // Clear temporary selections and close modal
    setTemporarySelections({ billing: null, shipping: null });
    onClose(true);
  }, [temporarySelections, applySuggestionCheckout, onClose]);

  // Clear temporary selections when modal is closed by other means
  const handleModalClose = useCallback(() => {
    setTemporarySelections({ billing: null, shipping: null });
    onClose();
  }, [onClose]);

  const handleSuggestionSelect = useCallback(
    (suggestion: LoqateVerifyMatch, addressType: "billing" | "shipping") => {
      setBypassValidation(false);
      setTemporarySelections((prev) => ({
        ...prev,
        [addressType]:
          prev[addressType]?.suggestion === suggestion
            ? null
            : { suggestion, addressType },
      }));
    },
    [setBypassValidation]
  );

  // Handle proceeding without applying any suggestion
  const handleProceedAnyway = useCallback(() => {
    setBypassValidation(true);
    setTemporarySelections({ billing: null, shipping: null });
  }, [setBypassValidation]);


  // Handle escape key and focus management
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleModalClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, handleModalClose]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      {/* Desktop Modal (>991px) */}
      <div
        className="fixed wv-address-suggestion-modal modal-overlay w-100 h-100 align-items-center justify-content-center d-none d-lg-flex"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title-desktop"
        aria-describedby="modal-description-desktop"
      >
        {/* Backdrop */}
        <div
          className="position-absolute w-100 h-100"
          onClick={handleModalClose}
          aria-hidden="true"
        />

        {/* Modal Content */}
        <div
          ref={modalRef}
          className="p-0 mx-3 bg-white rounded modal-content position-relative"
          style={{
            maxWidth: "600px",
            maxHeight: "80vh",
            width: "100%",
            minHeight: "300px",
          }}
        >
          {/* Header */}
          <AddressSuggestionHeader $pt={$pt} onClose={handleModalClose} />

          {/* Body */}
          <AddressSuggestionContent
            modalData={modalData}
            hasMultipleAddresses={hasMultipleAddresses}
            temporarySelections={temporarySelections}
            onSuggestionSelect={handleSuggestionSelect}
            onProceedAnyway={handleProceedAnyway}
            bypassValidation={bypassValidation}
            descriptionId="modal-description-desktop"
            $pt={$pt}
          />

          {/* Footer */}
          <AddressSuggestionFooter
            hasValidSelections={hasValidSelections}
            onClose={handleModalClose}
            onDone={handleDoneClick}
            $pt={$pt}
            bypassValidation={bypassValidation}
          />
        </div>
      </div>

      {/* Mobile Popup (≤991px) */}
      <div
        className="popup popup--open d-lg-none wv-address-suggestion-modal"
        data-popup="checkout.addresses-match-suggestions"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title-mobile"
        aria-describedby="modal-description-mobile"
      >
        {/* Backdrop */}
        <div
          onClick={handleModalClose}
          className="popup__overlay"
          style={{ visibility: "hidden" }}
        ></div>

        {/* Mobile Popup Content */}
        <div className="popup__inner">
          {/* Header */}
          <AddressSuggestionHeader $pt={$pt} onClose={handleModalClose} />

          {/* Body */}
          <AddressSuggestionContent
            modalData={modalData}
            hasMultipleAddresses={hasMultipleAddresses}
            temporarySelections={temporarySelections}
            onSuggestionSelect={handleSuggestionSelect}
            onProceedAnyway={handleProceedAnyway}
            bypassValidation={bypassValidation}
            descriptionId="modal-description-mobile"
            $pt={$pt}
          />

          {/* Footer */}
          <AddressSuggestionFooter
            hasValidSelections={hasValidSelections}
            onClose={handleModalClose}
            onDone={handleDoneClick}
            $pt={$pt}
            bypassValidation={bypassValidation}
          />
        </div>
      </div>
    </>
  );
};

export default WvAddressSuggestionModal;
